import { type FC, useCallback, useEffect } from 'react';

import { useConfig } from 'config';
import type { DepositAmountTemplate } from 'config/types';
import { useClientProfile } from 'store';
import DepositItem from './DepositItem';

import styles from './PaymentForm.module.scss';

type DepositAmountTemplateList = {
  onSelectDepositTemplate: (selectedDepositId: number) => void;
  fieldValue?: number;
};

const DepositAmountTemplateList: FC<DepositAmountTemplateList> = ({
  onSelectDepositTemplate,
  fieldValue,
}) => {
  const [clientProfile] = useClientProfile();
  const depositAmountTemplates = useConfig(
    'config.siteConfig.payments.depositAmountTemplates',
  );

  const currency = clientProfile.data?.client.currency;

  const depositOptions =
    depositAmountTemplates && currency
      ? depositAmountTemplates[currency]
      : null;

  const handleSelect = useCallback(
    (item: number) => {
      onSelectDepositTemplate(item);
    },
    [onSelectDepositTemplate],
  );

  useEffect(() => {
    if (currency && depositAmountTemplates) {
      const paymentItem = depositAmountTemplates[currency]?.find(
        (item) => item.isDefault,
      );

      if (paymentItem) {
        /* queueMicrotask is used here to defer the call to onSelectDepositTemplate until
           after the current synchronous code has executed. This prevents timing issues where
           the form's fieldValue hasn't updated yet, ensuring the default value is correctly applied.
         */
        queueMicrotask(() =>
          onSelectDepositTemplate(Number(paymentItem.value)),
        );
      }
    }
  }, [currency, onSelectDepositTemplate, depositAmountTemplates]);

  const isSelected = useCallback(
    (item: DepositAmountTemplate) => {
      return fieldValue === Number(item.value);
    },

    [fieldValue],
  );

  if (!depositOptions) {
    return null;
  }

  return (
    <div className={styles['payment-templates']}>
      {depositOptions.map((item) => (
        <DepositItem
          key={item.value}
          item={Number(item.value)}
          onSelect={handleSelect}
          isSelected={isSelected(item)}
        />
      ))}
    </div>
  );
};

export default DepositAmountTemplateList;
